import { SelectHTMLAttributes, forwardRef } from 'react'

interface SelectOption {
  value: string
  label: string
}

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string
  hint?: string
  error?: string
  example?: string
  options: SelectOption[]
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, hint, error, example, options, className = '', id, ...rest }, ref) => {
    const fieldId = id ?? label?.toLowerCase().replace(/\s+/g, '-')
    return (
      <>
        <div className={`field ${error ? 'field--error' : ''} ${className}`}>
          {label && <label className="field-label" htmlFor={fieldId}>{label}</label>}
          {example && <p className="field-example">e.g. {example}</p>}
          <div className="select-wrap">
            <select ref={ref} id={fieldId} className="select" {...rest}>
              {options.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <span className="select-chevron">▾</span>
          </div>
          {hint && !error && <p className="field-hint">{hint}</p>}
          {error && <p className="field-error-msg">{error}</p>}
        </div>

        <style>{`
          .select-wrap { position: relative; }
          .select {
            width: 100%;
            padding: 8px 36px 8px 12px;
            background: var(--bg-input);
            border: 1px solid var(--border);
            border-radius: var(--radius-md);
            color: var(--text-primary);
            font-size: 0.875rem;
            font-family: var(--font-sans);
            appearance: none;
            cursor: pointer;
            outline: none;
            transition: border-color 0.15s;
          }
          .select:focus { border-color: var(--accent); }
          .field--error .select { border-color: var(--color-error); }
          .select option { background: var(--bg-surface); }
          .select-chevron {
            position: absolute;
            right: 12px;
            top: 50%;
            transform: translateY(-50%);
            color: var(--text-muted);
            pointer-events: none;
            font-size: 0.75rem;
          }
        `}</style>
      </>
    )
  }
)
Select.displayName = 'Select'
